import { useMemo, type ReactNode } from 'react';
import { Megaphone } from 'lucide-react';

import { CompactNoticeCard } from '@/components/notices/CompactNoticeCard';
import { EmptyState } from '@/components/ui/EmptyState';
import type { FigureTone } from '@/components/ui/FigureTile';
import type { ParentNotice } from '../../types/overview';
import { OverviewCard } from './OverviewCard';

export interface NoticeListProps {
  title: string;
  icon: ReactNode;
  tone?: FigureTone;
  notices: ParentNotice[];
  /**
   * How many the whole board holds. The server sends only the latest few, so
   * `notices.length` would count the preview rather than the board.
   */
  total: number;
  /** Where the whole board lives. */
  viewAllTo: string;
  viewAllLabel: string;
  emptyTitle: string;
  emptyDescription: string;
  /** Caps a preview card; the board itself carries the rest. */
  limit?: number;
}

export function NoticeList({
  title,
  icon,
  tone = 'accent',
  notices,
  total,
  viewAllTo,
  viewAllLabel,
  emptyTitle,
  emptyDescription,
  limit = 3,
}: NoticeListProps) {
  const sortedNotices = useMemo(() => {
    return [...notices].sort((a, b) => {
      const dateCompare = (b.noticeDate || '').localeCompare(a.noticeDate || '');
      if (dateCompare !== 0) return dateCompare;
      const postedA = a.postedAt || '';
      const postedB = b.postedAt || '';
      if (postedA && postedB) return postedB.localeCompare(postedA);
      return 0;
    });
  }, [notices]);

  const preview = sortedNotices.slice(0, limit);

  return (
    <OverviewCard
      title={title}
      icon={icon}
      tone={tone}
      count={total}
      viewAllTo={total > 0 ? viewAllTo : undefined}
      viewAllLabel={viewAllLabel}
    >
      {preview.length === 0 ? (
        <EmptyState
          icon={<Megaphone className="h-5 w-5" />}
          title={emptyTitle}
          description={emptyDescription}
          className="py-8"
        />
      ) : (
        preview.map((notice) => (
          <CompactNoticeCard key={notice.uuid} notice={notice} audience={notice.audience} />
        ))
      )}
    </OverviewCard>
  );
}
